const ROLES_REALM = 'https://verrific.us/legitimation';
const ORGANIZATION_REALM = 'https://verrific.us/organization';
const OFFICE_ID_REALM = 'https://verrific.us/officeId';
const INTEGRATION_REALM = 'https://verrific.us/scheduleCredentials';
const OUTSOURCE_ENABLED = 'https://verrific.us/outsourceEnabled';
const PAYMENT_TYPE = 'https://verrific.us/paymentType';
const ABLE_TO_FULLFILLFORM = 'https://verrific.us/ableToFullfillForm';
const EDIT_GLOBAL_CUSTOMIZATION_ID_REALM = 'https://verrific.us/canEditGlobalCustomization';
const EDIT_GLOBAL_CARRIERS_ID_REALM = 'https://verrific.us/canEditGlobalCarriers';
const GET_ALLOWED_DENTAL_OFFICES = 'https://verrific.us/officeIds';

export type TAuthRoles = 'admin' | 'agent' | 'superadministrator' | 'staff' | 'staffmanager' | 'doregistration' |'goduser' | 'groupmanager';

const availableIntegrations = ['Open Dental', 'Dentrix'];

export const enum RolesEnum {
  // plus privs
  GODUSER = 'goduser',
  SUPERADMIN = 'superadministrator',
  ADMIN = 'admin',
  AGENT = 'agent',

  // dental office privs
  STAFF = 'staff',
  STAFFMANAGER = 'staffmanager',
  DO_GROUP_MANAGER = 'groupmanager',
  DO_GROUP_MEMBER = 'groupmember',
  
  // dental office registration privs
  DOREGISTRATION = 'doregistration',
}

export interface IOfficeSettings {
  name: string;
  id: string;
  scheduleIntegration?: {
    type: string;
    pmsType: string;
    pmsVersion?: string;
    integration?: {
      subdomain: string,
      locastionId: number
    }
  },
  outsource: {
    company: string;
    outsourceEnabled: boolean;
  },
  ableToFullfillForm: boolean;
  paymentType: string;
}

declare const window: {
  sessionStorage: {
    getItem: (key: string) => string | null;
    hasItem: (key: string) => boolean;
  };
};

class Auth0VerrificImpl {
  private static __instance: Auth0VerrificImpl;
  static getInstance(): Auth0VerrificImpl {
    if (!Auth0VerrificImpl.__instance) {
      Auth0VerrificImpl.__instance = new Auth0VerrificImpl();
    }
    return Auth0VerrificImpl.__instance;
  }
  private token?: {[key: string]: any};
  private officesSettings: IOfficeSettings[] | undefined;
  private postLoginAction: (() => void) | null = null;

  private constructor() {}

  private is(role: string) {
    const isAdmin = Array.isArray(this.token?.[ROLES_REALM]);
    let clearListOfRoles: string[];
    if (isAdmin) {
      clearListOfRoles = this.token?.[ROLES_REALM]
        ?.map((el: string) => el.toLowerCase().split(' ').join('')) || [];
    } else {
      clearListOfRoles = this.token?.[ROLES_REALM]?.[this.getOfficeId()]
        ?.map((el: string) => el.toLowerCase().split(' ').join('')) || [];
      
    }
    const normalizedRole = role.toLowerCase().split(' ').join('');
    return clearListOfRoles.includes(normalizedRole);
  }

  private decodeJWT(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(Buffer.from(base64, 'base64').toString().split('').map((character) => {
      return '%' + ('00' + character.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    const decoded =  JSON.parse(jsonPayload);
    decoded.__raw = token;
    return decoded;
  };

  setToken(token: {[key: string]: any}) {
    this.token = token;
    if (this.postLoginAction) {
      this.postLoginAction();
    }
  }

  setRawToken(token: string) {
    this.token = this.decodeJWT(token);
    if (this.postLoginAction) {
      this.postLoginAction();
    }
  }

  setOfficesSettings(settings?: IOfficeSettings[]) {
    this.officesSettings = settings;
  }

  setPostLoginAction(action: () => void) {
    this.postLoginAction = action;
  }

  getToken() {
    return this.token?.__raw;
  }

  getExpirationDate() {
    return new Date(this.token?.exp ? this.token?.exp * 1000 : new Date());
  }

  getRoles() {
    let realmData = this.token?.[ROLES_REALM];
    const isDo = !Array.isArray(realmData);
    if (isDo) {
      realmData = realmData?.[this.getOfficeId()]
    }
    return realmData?.map((el: string) => el.toLowerCase().split(' ').join('')) || [];
  }

  getIntegration() {
    if (!this.officesSettings) {
      return undefined;
    }
    return this.officesSettings
      .find((office: IOfficeSettings) => office.id === this.getOfficeId())
      ?.scheduleIntegration
  }

  getIntegrationType() {
    return this.getIntegration()?.type
  }

  getIntegrationPMSType() {
    return this.getIntegration()?.pmsType
  }

  getIntegrationData() {
    return this.getIntegration()?.integration
  }

  getOutsource() {
    if (!this.officesSettings) {
      return undefined;
    }
    return this.officesSettings
      ?.find((office: IOfficeSettings) => office.id === this.getOfficeId())
      ?.outsource;
  }

  getOutsourceAbility() {
    return this.getOutsource()?.outsourceEnabled;
  }

  getPaymentType() {
    if (!this.officesSettings) {
      return undefined;
    }
    return this.officesSettings
      ?.find((office: IOfficeSettings) => office.id === this.getOfficeId())
      ?.paymentType;
  }

  getAbilityToFullfillForm() {
    if (this.isAgent() || this.isAdmin() || this.isSuperAdmin() || this.isGodModeUser()) {
      return true;
    }
    if (!this.officesSettings) {
      return undefined;
    }
    return this.officesSettings
      ?.find((office: IOfficeSettings) => office.id === this.getOfficeId())
      ?.ableToFullfillForm;
  }

  isGroupManager() {
    return this.is(RolesEnum.DO_GROUP_MANAGER);
  }

  isGroupMember() {
    return this.is(RolesEnum.DO_GROUP_MEMBER);
  }

  isSuperAdmin() {
    return this.is(RolesEnum.SUPERADMIN);
  }

  isAdmin() {
    return this.is(RolesEnum.ADMIN);
  }

  isAgent() {
    return this.is(RolesEnum.AGENT);
  }

  isGodModeUser() {
    return this.is(RolesEnum.GODUSER);
  }

  isStaff() {
    return this.is(RolesEnum.STAFF);
  }

  isStaffManager() {
    return this.is(RolesEnum.STAFFMANAGER);
  }

  getUserMetadata() {
    return this.token?.app_metadata;
  }

  getUserData() {
    return this.token;
  }

  getOfficeId() {
    if (window) {
      let officeId = window.sessionStorage.getItem('currentDentalOffice');
      if (!officeId || officeId === 'undefined') {
        officeId = this.token?.[GET_ALLOWED_DENTAL_OFFICES]?.[0];
      }
      return officeId;
    } else {
      return this.token?.[OFFICE_ID_REALM];
    }
    
  }

  getEditGlobalCustomizationPrivilage() {
    return this.token?.[EDIT_GLOBAL_CUSTOMIZATION_ID_REALM];
  }

  getEditGlobalCarriersPrivilage() {
    return this.token?.[EDIT_GLOBAL_CARRIERS_ID_REALM];
  }

  getAllowedDentalOffices() {
    return this.token?.[GET_ALLOWED_DENTAL_OFFICES];
  }

  isDODuringRegistration() {
    return !!this.getToken() && !this.isStaff() && !this.isStaffManager() && !this.getOfficeId() && !this.isCompanyAuthorized();
  }

  isAuthorized() {
    return !!this.getToken() && (this.isAgent() || this.isAdmin() || this.isSuperAdmin() || this.isStaff() || this.isStaffManager() || this.isGodModeUser());
  }

  isCompanyAuthorized() {
    return !!this.getToken() && (this.isAgent() || this.isAdmin() || this.isSuperAdmin() || this.isGodModeUser());
  }

  isDentalOfficeAuthorized() {
    return !!this.getToken() && (this.isStaff() || this.isStaffManager());
  }

  isIntegrated() {
    return !!this.getIntegration();
  }

  isPMSIntegrated() {
    if (!this.officesSettings) {
      return false;
    }
    const integrationType = this.getIntegrationPMSType();
    if (!integrationType) {
      return false;
    }
    return availableIntegrations.includes(integrationType);
  }

  getOrganization() {
    return this.token?.[ORGANIZATION_REALM];
  }
}

export const Auth0Verrific = Auth0VerrificImpl.getInstance();